import striptags from 'striptags'

/**
 * Tags kept when pasting into the editor. Everything else (headings, tables, styles) is stripped
 * down to its text by `striptags`.
 */
export const ALLOWED_PASTE_TAGS = [
  'b',
  'u',
  'i',
  'ins',
  'em',
  'br',
  'strong',
  'del',
  'p',
  'ul',
  'ol',
  'li',
  'img',
  'div',
  'span',
  'mark',
  'a'
]

// `Extended_Pictographic` alone misses keycaps (`#️⃣` is `#` + U+FE0F + U+20E3) and flags (two
// regional indicators), so each gets its own test. Three separate patterns rather than one
// alternation or one character class: lint rejects both of those shapes here.
// safe-regex cannot parse `\p{...}` or `\u{...}`, so it reports all three of these as unsafe. Each
// is a single character match with no quantifier, so none can backtrack.
/* eslint-disable security/detect-unsafe-regex */
const PICTOGRAPHIC_REGEX = /\p{Extended_Pictographic}/u
const REGIONAL_INDICATOR_REGEX = /[\u{1F1E6}-\u{1F1FF}]/u
const KEYCAP_REGEX = /\u{20E3}/u
/* eslint-enable security/detect-unsafe-regex */

/** Attributes that hold the emoji as literal text, highest priority first. */
const TEXT_ATTRIBUTES = ['data-stringify-emoji', 'data-emoji', 'alt', 'aria-label', 'title']

/** Attributes that hold the emoji as hyphen-separated hex code points, e.g. Gmail's `goomoji`. */
const CODE_POINT_ATTRIBUTES = ['goomoji', 'data-emoji-unified', 'data-codepoints']

/** One hex code point of a Twemoji-style filename, e.g. the `1f389` in `1f389.svg`. */
const SINGLE_CODE_POINT_REGEX = /^[0-9a-f]{4,6}$/

/**
 * True for a bare code-point sequence such as `1f389` or `1f1e6-1f1e8`. Split first, then test each
 * part: one regex over the whole sequence needs a nested quantifier, which is a ReDoS shape on
 * pasted input we do not control.
 */
function isCodePointSequence(value: string): boolean {
  return value.split(/[-_]/).every((part) => SINGLE_CODE_POINT_REGEX.test(part))
}

/** True when the value holds at least one emoji character. */
function isEmoji(value: string): boolean {
  return PICTOGRAPHIC_REGEX.test(value) || REGIONAL_INDICATOR_REGEX.test(value) || KEYCAP_REGEX.test(value)
}

/**
 * Turns `1f389`, `1f1e6-1f1e8` or `U+1F389` into the characters they name. Returns null when the
 * value is not a code-point sequence, or names something outside the Unicode range.
 */
function codePointsToText(raw: string | null): string | null {
  if (!raw) {
    return null
  }

  const normalized = raw.trim().toLowerCase().replace(/\s+/g, '-')
  const parts = normalized
    .split(/[-_]/)
    .map((part) => part.replace(/^u\+?/, ''))
    .filter((part) => part.length > 0)

  if (!parts.length || !parts.every((part) => /^[0-9a-f]{1,6}$/.test(part))) {
    return null
  }

  // Reject controls, lone surrogates and anything past the Unicode range, so `String.fromCodePoint`
  // always gets a value it accepts. The floor stays at 0x20 on purpose: a valid emoji sequence can
  // hold code points that are not emoji themselves. `#️⃣` is `0023-fe0f-20e3`, and 0x23 is `#`. A
  // floor at the first emoji block (0x231A) would drop every keycap. `isEmoji` decides intent
  // afterwards, on the assembled string, so this function only needs to keep `fromCodePoint` legal.
  const codePoints = parts.map((part) => parseInt(part, 16))
  const isUsable = (codePoint: number) =>
    codePoint >= 0x20 && codePoint <= 0x10ffff && !(codePoint >= 0xd800 && codePoint <= 0xdfff)

  if (!codePoints.every(isUsable)) {
    return null
  }

  return String.fromCodePoint(...codePoints)
}

/** The `1f389` in `https://cdn.example.com/svg/1f389.svg`. */
function codePointsFromSrc(src: string | null): string | null {
  if (!src) {
    return null
  }

  const fileName = src.split(/[?#]/)[0].split('/').pop()
  if (!fileName) {
    return null
  }

  const baseName = fileName.replace(/\.[a-z0-9]+$/i, '').toLowerCase()
  if (!isCodePointSequence(baseName)) {
    return null
  }

  return codePointsToText(baseName)
}

/** The emoji this `<img>` stands in for, or null when it is a real image. */
function emojiForImage(image: Element): string | null {
  for (const attribute of TEXT_ATTRIBUTES) {
    const value = image.getAttribute(attribute)
    if (value && isEmoji(value)) {
      return value
    }
  }

  for (const attribute of CODE_POINT_ATTRIBUTES) {
    const text = codePointsToText(image.getAttribute(attribute))
    if (text && isEmoji(text)) {
      return text
    }
  }

  const fromSrc = codePointsFromSrc(image.getAttribute('src'))
  return fromSrc && isEmoji(fromSrc) ? fromSrc : null
}

/**
 * Replaces emoji `<img>` elements with the emoji character.
 *
 * Gmail (`goomoji`), Slack (`data-stringify-emoji`) and WordPress/Twemoji (`class="emoji"`) all put
 * emoji on the clipboard as an `<img>`. The editor registers no TipTap `Image` extension, so
 * ProseMirror's schema has no `image` node and drops those elements — and the emoji with them.
 *
 * A real photo or a tracking pixel is left exactly as it was, because the substitution only runs
 * when the candidate text is an emoji.
 */
export function emojiImagesToText(html: string): string {
  if (!html || !/<img\b/i.test(html)) {
    return html
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const images = Array.from(doc.body.querySelectorAll('img'))

  let replaced = false
  for (const image of images) {
    const emoji = emojiForImage(image)
    if (emoji) {
      image.replaceWith(doc.createTextNode(emoji))
      replaced = true
    }
  }

  return replaced ? doc.body.innerHTML : html
}

/**
 * The editor's paste transform: emoji images become text, then disallowed tags are stripped.
 *
 * `striptags` is the sanitisation boundary, and it must stay last. `emojiImagesToText` only rewrites
 * `<img>` elements; it removes nothing and grants nothing, and its `DOMParser` round-trip never
 * executes script. Order matters: the DOM step normalises the markup, so `striptags` then reads
 * well-formed tags instead of whatever shape the source app put on the clipboard.
 *
 * `striptags` removes tags by name, not attributes, and it keeps the text inside a removed tag. That
 * is unchanged by this file. `HtmlContent` runs the same allowlist again at render time.
 */
export function transformPastedHtml(html: string): string {
  return striptags(emojiImagesToText(html), ALLOWED_PASTE_TAGS)
}
